import React, { useState, useEffect } from 'react';
import {
  OrganizationRegulation,
  ComplianceRequirement,
  RegulatoryChangeRecord,
  RegulationsDashboardMetrics,
  ComplianceStatus,
  MappingType,
  ControlStrength,
  RegulatoryChangeLifecycle,
} from './types';
import { regulationsService } from './regulationsService';
import { ComplianceMetricsDashboard } from './ComplianceMetricsDashboard';
import { RegulationLibraryTable } from './RegulationLibraryTable';
import { ComplianceRequirementsList } from './ComplianceRequirementsList';
import { ComplianceRequirementDetailModal } from './ComplianceRequirementDetailModal';
import { RegulatoryChangeRegister } from './RegulatoryChangeRegister';

export const RegulationsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'METRICS' | 'LIBRARY' | 'REQUIREMENTS' | 'CHANGES'>('METRICS');
  const [metrics, setMetrics] = useState<RegulationsDashboardMetrics | null>(null);
  const [regulations, setRegulations] = useState<OrganizationRegulation[]>([]);
  const [requirements, setRequirements] = useState<ComplianceRequirement[]>([]);
  const [changes, setChanges] = useState<RegulatoryChangeRecord[]>([]);
  const [selectedRequirement, setSelectedRequirement] = useState<ComplianceRequirement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [m, r, reqs, chgs] = await Promise.all([
        regulationsService.getMetrics(),
        regulationsService.getRegulations(),
        regulationsService.getRequirements(),
        regulationsService.getRegulatoryChanges(),
      ]);
      setMetrics(m);
      setRegulations(r);
      setRequirements(reqs);
      setChanges(chgs);
    } catch (err: any) {
      setError(err.message || 'Veriler yuklenirken bir hata meydana geldi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Handlers
  const handleCreateRegulation = async (data: Partial<OrganizationRegulation>) => {
    try {
      await regulationsService.createRegulation(data);
      loadData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleCreateRequirement = async (data: Partial<ComplianceRequirement>) => {
    try {
      await regulationsService.createRequirement(data);
      loadData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleSelectRequirement = async (req: ComplianceRequirement) => {
    try {
      const detailed = await regulationsService.getRequirementById(req.id);
      setSelectedRequirement(detailed);
    } catch (err) {
      setSelectedRequirement(req);
    }
  };

  const handleRecordAssessment = async (data: {
    assessor_name: string;
    assessor_role: string;
    status_assigned: ComplianceStatus;
    why_compliant?: string;
    gap_analysis?: string;
    evidence_summary?: string;
    finding_reference?: string;
    capa_reference?: string;
  }) => {
    if (!selectedRequirement) return;
    try {
      await regulationsService.recordAssessment(selectedRequirement.id, data);
      const updated = await regulationsService.getRequirementById(selectedRequirement.id);
      setSelectedRequirement(updated);
      loadData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleAddMapping = async (data: {
    mapping_type: MappingType;
    target_reference_id: string;
    target_title: string;
    control_strength: ControlStrength;
    created_by: string;
  }) => {
    if (!selectedRequirement) return;
    try {
      await regulationsService.addMapping(selectedRequirement.id, data);
      const updated = await regulationsService.getRequirementById(selectedRequirement.id);
      setSelectedRequirement(updated);
      loadData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleRemoveMapping = async (mappingId: string) => {
    if (!selectedRequirement) return;
    try {
      await regulationsService.removeMapping(mappingId);
      const updated = await regulationsService.getRequirementById(selectedRequirement.id);
      setSelectedRequirement(updated);
      loadData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleAddSourceLink = async (canonicalId: string, notes?: string) => {
    if (!selectedRequirement) return;
    try {
      await regulationsService.addSourceLink(selectedRequirement.id, canonicalId, notes);
      const updated = await regulationsService.getRequirementById(selectedRequirement.id);
      setSelectedRequirement(updated);
      loadData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleCreateChange = async (data: Partial<RegulatoryChangeRecord>) => {
    try {
      await regulationsService.createRegulatoryChange(data);
      loadData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleTransitionChange = async (
    id: string,
    newStatus: RegulatoryChangeLifecycle,
    notes?: string
  ) => {
    await regulationsService.transitionRegulatoryChange(id, newStatus, notes);
    loadData();
  };

  const handleAddChangeAction = async (
    changeId: string,
    data: {
      title: string;
      action_type: any;
      assigned_department: string;
      assignee: string;
      due_date: string;
    }
  ) => {
    try {
      await regulationsService.addChangeAction(changeId, data);
      loadData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleUpdateChangeAction = async (actionId: string, status: string, notes?: string) => {
    try {
      await regulationsService.updateChangeAction(actionId, { status, evidence_notes: notes });
      loadData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="page-header-banner">
        <div className="badge">REGULATORY & COMPLIANCE</div>
        <h1>Corporate Regulations & Compliance Management</h1>
        <p>Regulatory library, internal compliance requirements, periodic assessments, and closed-loop regulation change management.</p>
      </div>

      {/* Global Navigation Tabs */}
      <div className="nav-tabs">
        <button
          onClick={() => setActiveTab('METRICS')}
          className={`tab-btn ${activeTab === 'METRICS' ? 'active' : ''}`}
        >
          Summary & Metrics
        </button>
        <button
          onClick={() => setActiveTab('LIBRARY')}
          className={`tab-btn ${activeTab === 'LIBRARY' ? 'active' : ''}`}
        >
          Regulation Library ({regulations.length})
        </button>
        <button
          onClick={() => setActiveTab('REQUIREMENTS')}
          className={`tab-btn ${activeTab === 'REQUIREMENTS' ? 'active' : ''}`}
        >
          Internal Requirements ({requirements.length})
        </button>
        <button
          onClick={() => setActiveTab('CHANGES')}
          className={`tab-btn ${activeTab === 'CHANGES' ? 'active' : ''}`}
        >
          Change Management ({changes.length})
        </button>
      </div>

      {error && (
        <div className="p-4 bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded-xl">
          ⚠️ {error}
        </div>
      )}

      {/* Main Tab Content */}
      {activeTab === 'METRICS' && (
        <ComplianceMetricsDashboard metrics={metrics} loading={loading} />
      )}

      {activeTab === 'LIBRARY' && (
        <RegulationLibraryTable
          regulations={regulations}
          onSelectRegulation={(reg) => {
            setActiveTab('REQUIREMENTS');
          }}
          onCreateRegulation={handleCreateRegulation}
        />
      )}

      {activeTab === 'REQUIREMENTS' && (
        <ComplianceRequirementsList
          requirements={requirements}
          onSelectRequirement={handleSelectRequirement}
          onCreateRequirement={handleCreateRequirement}
        />
      )}

      {activeTab === 'CHANGES' && (
        <RegulatoryChangeRegister
          changes={changes}
          onCreateChange={handleCreateChange}
          onTransitionChange={handleTransitionChange}
          onAddAction={handleAddChangeAction}
          onUpdateAction={handleUpdateChangeAction}
        />
      )}

      {/* Detailed Modal */}
      {selectedRequirement && (
        <ComplianceRequirementDetailModal
          requirement={selectedRequirement}
          onClose={() => setSelectedRequirement(null)}
          onRecordAssessment={handleRecordAssessment}
          onAddMapping={handleAddMapping}
          onRemoveMapping={handleRemoveMapping}
          onAddSourceLink={handleAddSourceLink}
        />
      )}
    </div>
  );
};
